import React from 'react';
import {
  Button,
  CheckCircleIcon,
  CloseIcon,
  ErrorOutlineIcon,
  ReplayIcon,
  Text,
  WarningAmberIcon,
} from '@billda/design-system';
import type { UploadItem } from '../../stores/uploadTrayStore';
import { formatFileSize } from './formatFileSize';

interface UploadTrayItemProps {
  item: UploadItem;
  onRetry: (localId: string) => void;
  onRemove: (localId: string) => void;
  onResolveCollision: (localId: string, action: 'keep-both' | 'skip') => void;
}

function formatSpeed(bytesPerSec: number): string {
  if (bytesPerSec >= 1_048_576) {
    return `${(bytesPerSec / 1_048_576).toFixed(1)} MB/s`;
  }
  return `${(bytesPerSec / 1024).toFixed(0)} KB/s`;
}

export const UploadTrayItem: React.FC<UploadTrayItemProps> = ({
  item,
  onRetry,
  onRemove,
  onResolveCollision,
}) => {
  const isCollision = item.status === 'collision';
  const isInProgress =
    item.status === 'uploading' ||
    item.status === 'requesting-url' ||
    item.status === 'confirming';
  const isFailed = item.status === 'failed';
  const isCompleted = item.status === 'completed';

  return (
    <div
      data-testid="upload-tray-item"
      className="border-b border-border-default px-4 py-2 last:border-b-0"
    >
      <div className="flex items-center gap-2">
        <Text variant="body2" noWrap className="flex-1">
          {item.fileName}
        </Text>

        {isCompleted && (
          <div className="flex items-center gap-1">
            <Text variant="caption" color="secondary">
              {formatFileSize(item.file.size)}
            </Text>
            <CheckCircleIcon
              className="size-4.5 text-content-success"
              aria-hidden="true"
            />
          </div>
        )}

        {isFailed && (
          <div className="flex items-center gap-0.5">
            <Button
              data-testid="upload-tray-item-retry"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              aria-label="Retry upload"
              onClick={() => onRetry(item.localId)}
            >
              <ReplayIcon className="size-4.5" aria-hidden="true" />
            </Button>
            <Button
              data-testid="upload-tray-item-remove"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              aria-label="Remove upload"
              onClick={() => onRemove(item.localId)}
            >
              <CloseIcon className="size-4.5" aria-hidden="true" />
            </Button>
          </div>
        )}
      </div>

      {isInProgress && (
        <div className="mt-1">
          {item.status === 'uploading' ? (
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(item.progress)}
              className="h-1 w-full overflow-hidden rounded-full bg-surface-disabled"
            >
              <div
                className="h-full rounded-full bg-surface-primary transition-[width]"
                style={{ width: `${item.progress}%` }}
              />
            </div>
          ) : (
            <div className="h-1 w-full animate-pulse rounded-full bg-surface-primary" />
          )}
          {item.status === 'uploading' && item.speed > 0 && (
            <div className="mt-0.5 flex justify-between">
              <Text variant="caption" color="secondary">
                {formatSpeed(item.speed)}
              </Text>
              {item.eta > 0 && (
                <Text variant="caption" color="secondary">
                  ~{item.eta}s remaining
                </Text>
              )}
            </div>
          )}
        </div>
      )}

      {isFailed && (
        <div className="mt-1 flex items-center gap-1">
          <ErrorOutlineIcon
            className="size-3.5 text-content-error"
            aria-hidden="true"
          />
          <Text variant="caption" className="text-content-error">
            {item.error || 'Upload failed'}
          </Text>
        </div>
      )}

      {isCollision && (
        <div className="mt-1">
          <div className="flex items-center gap-1">
            <WarningAmberIcon
              className="size-3.5 text-content-warning"
              aria-hidden="true"
            />
            <Text variant="caption" color="secondary">
              A file with this name already exists
            </Text>
          </div>
          <div className="mt-1 flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onResolveCollision(item.localId, 'keep-both')}
              data-testid="collision-keep-both"
            >
              Keep both
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onResolveCollision(item.localId, 'skip')}
              data-testid="collision-skip"
            >
              Skip
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
